import { asPartialDef } from "../coerce";
import type { BlockPlugin, GradeResult, ValidationResult } from "../types";

/**
 * "Complete code". DETERMINISTIC grading without executing arbitrary code:
 * the author declares `checks` (contains / regex / normalized equality / does-not-contain)
 * that the submission must satisfy. Extensible to a sandboxed runner as a separate
 * plugin without touching the engine.
 */
export type CodeCheck =
  | { kind: "contains"; value: string }
  | { kind: "not-contains"; value: string }
  | { kind: "regex"; value: string; flags?: string }
  | { kind: "equals"; value: string };

export interface CodeDef {
  prompt: string;
  language: string;
  /** Reference solution (for revealing). */
  solution?: string;
  checks: CodeCheck[];
  /** Ignore whitespace/indentation when comparing `equals` (default true). */
  ignoreWhitespace?: boolean;
}

export interface CodeResponse {
  code: string;
}

function collapseWs(s: string): string {
  return s.replaceAll(/\s+/g, " ").trim();
}

function runCheck(check: CodeCheck, code: string, ignoreWs: boolean): boolean {
  switch (check.kind) {
    case "contains": {
      return code.includes(check.value);
    }
    case "not-contains": {
      return !code.includes(check.value);
    }
    case "equals": {
      if (ignoreWs) {
        return collapseWs(code) === collapseWs(check.value);
      }
      return code === check.value;
    }
    case "regex": {
      try {
        return new RegExp(check.value, check.flags).test(code);
      } catch {
        return false;
      }
    }
    default: {
      return false;
    }
  }
}

export const codeBlock: BlockPlugin<CodeDef, CodeResponse> = {
  type: "code",
  interaction: "code",
  autoGradable: true,

  validate(def: unknown): ValidationResult<CodeDef> {
    const d = asPartialDef<CodeDef>(def);
    const errors: string[] = [];
    if (!d) {
      return { ok: false, errors: ["code: definición no es un objeto"] };
    }
    if (typeof d.prompt !== "string" || d.prompt.trim() === "") {
      errors.push("code: `prompt` requerido");
    }
    if (typeof d.language !== "string" || d.language.trim() === "") {
      errors.push("code: `language` requerido");
    }
    if (!Array.isArray(d.checks) || d.checks.length === 0) {
      errors.push("code: al menos 1 `checks`");
    }
    if (errors.length > 0) {
      return { ok: false, errors };
    }
    return {
      ok: true,
      def: {
        prompt: d.prompt ?? "",
        language: d.language ?? "",
        solution: d.solution,
        checks: d.checks ?? [],
        ignoreWhitespace: d.ignoreWhitespace ?? true,
      },
    };
  },

  grade(def: CodeDef, response: CodeResponse | undefined): GradeResult {
    const code = response?.code ?? "";
    const ignoreWs = def.ignoreWhitespace ?? true;
    if (code.trim() === "") {
      return { correct: false, score: 0, maxScore: 1, revealed: def.solution };
    }
    let passed = 0;
    for (const check of def.checks) {
      if (runCheck(check, code, ignoreWs)) {
        passed += 1;
      }
    }
    const total = def.checks.length;
    const score = total === 0 ? 0 : passed / total;
    return { correct: passed === total, score, maxScore: 1, revealed: def.solution };
  },
};
